
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronDown, ChevronUp, AlertCircle, Upload, CalendarIcon, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

export default function DisputeQuestionsSection({ 
  selectedCardholder, 
  selectedTransactions, 
  onDisputeDataChange,
  disputeData,
  disputeReason,
  onPreview,
  disabled
}) {
  const [expanded, setExpanded] = useState(true);
  const [responses, setResponses] = useState(disputeData?.responses || {});
  const [showQuestions, setShowQuestions] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  
  // Reset when cardholder or transactions change
  useEffect(() => {
    if (!selectedCardholder || selectedTransactions.length === 0) {
      setResponses({});
      setShowQuestions(false);
    } else {
      setShowQuestions(true);
    }
  }, [selectedCardholder, selectedTransactions]);
  
  // Update parent component with dispute data changes
  useEffect(() => {
    if (disputeReason) {
      onDisputeDataChange({
        disputeCategory: disputeReason.category,
        disputeType: disputeReason.type,
        responses
      });
    }
  }, [disputeReason, responses, onDisputeDataChange]);
  
  // Check if settlement status is pending
  const hasPendingTransaction = selectedTransactions.some(t => t.settlement_status === "Pending");
  
  // Check if any transaction is expired (over 90 days)
  const hasExpiredTransaction = selectedTransactions.some(t => {
    const transactionDate = new Date(t.date);
    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(today.getDate() - 90);
    return transactionDate < ninetyDaysAgo;
  });

  const handleResponseChange = (question, value) => {
    setResponses({
      ...responses,
      [question]: value
    });
    
    // Clear validation error when answering
    if (validationErrors[question]) {
      setValidationErrors({
        ...validationErrors,
        [question]: null
      });
    }
  };

  const handleSkipQuestion = (question) => {
    const questionConfig = getQuestionsForType(disputeReason?.type).find(q => q.id === question);
    if (questionConfig?.type === 'radio') {
      handleResponseChange(question, "Cardholder chose not to answer");
    } else {
      setResponses({
        ...responses,
        [question]: "Cardholder chose not to answer"
      });
    }
    
    // Clear validation error when skipping
    if (validationErrors[question]) {
      setValidationErrors({
        ...validationErrors,
        [question]: null
      });
    }
  };

  const handleFileUpload = (questionId, e) => {
    const file = e.target.files[0];
    if (file) {
      // In a real app, you would upload this file to your server
      console.log(`File selected for ${questionId}:`, file.name);
      
      // Mock the upload response
      const fileUrl = URL.createObjectURL(file);
      
      setEvidenceFiles([
        ...evidenceFiles,
        { questionId, filename: file.name, url: fileUrl }
      ]);
      
      handleResponseChange(questionId, `File uploaded: ${file.name}`);
    }
  };

  const handleSkipFileUpload = (questionId) => {
    handleResponseChange(questionId, "Cardholder chose to send file via email");
  };

  const validateResponses = () => {
    const errors = {};
    let isValid = true;
    let firstErrorId = null;
    
    // Get questions for the selected dispute type
    const questions = getQuestionsForType(disputeReason?.type);
    
    // Check each required question
    questions.forEach(q => {
      if (!q.optional && (!responses[q.id] || responses[q.id] === "")) {
        // Skip validation for conditional questions if parent condition isn't met
        if (q.parentId && q.parentCondition) {
          const parentResponse = responses[q.parentId];
          if (parentResponse !== q.parentCondition) {
            return; // Skip this validation
          }
        }
        
        errors[q.id] = "This question requires an answer";
        isValid = false;
        if (!firstErrorId) firstErrorId = q.id;
      }
    });
    
    setValidationErrors(errors);
    return { isValid, firstErrorId };
  };

  const handlePreview = () => {
    const { isValid, firstErrorId } = validateResponses();
    
    if (isValid) {
      onPreview();
    } else {
      // Scroll to the first error
      const errorElement = document.querySelector(`[data-question-id="${firstErrorId}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const isReadyToPreview = () => {
    return disputeReason && Object.keys(responses).length > 0;
  };

  // Render questions based on dispute type
  const renderQuestions = () => {
    if (!disputeReason?.type) return null;
    
    const questions = getQuestionsForType(disputeReason.type);
    
    return (
      <div className="space-y-6 mt-4">
        {questions.map((question) => {
          // Skip questions that depend on a parent question if the condition isn't met
          if (question.parentId && responses[question.parentId] !== question.parentCondition) {
            return null;
          }

          const hasError = !!validationErrors[question.id];
          
          return (
            <div 
              key={question.id} 
              className={`p-4 border rounded-lg ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              data-error={hasError}
              data-question-id={question.id}
            >
              <div className="flex items-start justify-between mb-2">
                <Label 
                  htmlFor={question.id} 
                  className={`text-base font-medium ${hasError ? 'text-red-700' : ''}`}
                >
                  {question.text}
                  {!question.optional && <span className="text-red-500 ml-1">*</span>}
                </Label>
                
                {question.optional && question.type !== 'file' && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleSkipQuestion(question.id)}
                    className="text-xs"
                  >
                    Cardholder chose not to answer
                  </Button>
                )}
              </div>
              
              {question.type === 'radio' && (
                <div className="mt-3">
                  <RadioGroup 
                    value={responses[question.id] || ""}
                    onValueChange={(value) => handleResponseChange(question.id, value)}
                  >
                    {question.options.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2 py-1">
                        <RadioGroupItem id={`${question.id}-${option.value}`} value={option.value} />
                        <Label htmlFor={`${question.id}-${option.value}`}>{option.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
              
              {question.type === 'text' && (
                <Input
                  id={question.id}
                  value={responses[question.id] || ""}
                  onChange={(e) => handleResponseChange(question.id, e.target.value)}
                  className="mt-1"
                  placeholder={question.placeholder || "Enter your answer"}
                />
              )}
              
              {question.type === 'textarea' && (
                <Textarea
                  id={question.id}
                  value={responses[question.id] || ""}
                  onChange={(e) => handleResponseChange(question.id, e.target.value)}
                  className="mt-1"
                  placeholder={question.placeholder || "Enter your answer"}
                />
              )}
              
              {question.type === 'date' && (
                <div className="mt-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal ${!responses[question.id] ? "text-gray-400" : ""}`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {responses[question.id] ? format(new Date(responses[question.id]), "PPP") : "Select a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={responses[question.id] ? new Date(responses[question.id]) : undefined}
                        onSelect={(date) => handleResponseChange(question.id, date ? format(date, "yyyy-MM-dd") : "")}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              
              {/* File upload UI with email option */}
              {question.type === 'file' && (
                <div className="mt-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex gap-2">
                      <Input
                        id={question.id}
                        type="file"
                        className="hidden"
                        onChange={(e) => handleFileUpload(question.id, e)}
                      />
                      <Label
                        htmlFor={question.id}
                        className="cursor-pointer py-2 px-4 border border-gray-300 rounded-md flex items-center gap-2 hover:bg-gray-50 whitespace-nowrap"
                      >
                        <Upload className="h-4 w-4" />
                        Upload File
                      </Label>
                    </div>
                    
                    {/* Email option for file upload */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSkipFileUpload(question.id)}
                      className="text-xs whitespace-nowrap"
                    >
                      Cardholder chose to send file via email
                    </Button>
                    
                    {/* Show file name when uploaded */}
                    {responses[question.id] && (
                      <span className="text-sm text-green-600 mt-2 block w-full">
                        {responses[question.id]}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {hasError && (
                <div className="mt-2 text-sm text-red-600">
                  {validationErrors[question.id]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Helper function to get questions for each dispute type
  function getQuestionsForType(type) {
    switch (type) {
      case "Unauthorized transaction – Card in possession":
        return [
          {
            id: "prev_purchase",
            text: "Have you ever purchased something from this merchant before?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" },
              { value: "Cardholder chose not to answer", label: "Cardholder chose not to answer" }
            ],
            optional: true
          },
          {
            id: "card_possession",
            text: "Are you always in possession of your card?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "authorized_others",
            text: "Did you authorize anyone to use your card?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "authorized_who",
            text: "Who?",
            type: "text",
            optional: false,
            parentId: "authorized_others",
            parentCondition: "Yes"
          },
          {
            id: "know_who_made_transaction",
            text: "Do you know who made this transaction?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "transaction_who",
            text: "Who?",
            type: "text",
            optional: false,
            parentId: "know_who_made_transaction",
            parentCondition: "Yes"
          },
          {
            id: "card_storage",
            text: "Where is the card usually kept?",
            type: "radio",
            options: [
              { value: "Wallet", label: "Wallet" },
              { value: "Home", label: "Home" },
              { value: "Other", label: "Other" },
              { value: "Cardholder chose not to answer", label: "Cardholder chose not to answer" }
            ],
            optional: true
          },
          {
            id: "card_storage_other",
            text: "Where?",
            type: "text",
            optional: false,
            parentId: "card_storage",
            parentCondition: "Other"
          },
          {
            id: "recurring_transaction",
            text: "Is it possible this is a planned, recurring transaction?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" },
              { value: "Not Sure", label: "Not Sure" },
              { value: "Cardholder chose not to answer", label: "Cardholder chose not to answer" }
            ],
            optional: true
          },
          {
            id: "additional_details",
            text: "Any additional details worth mentioning?",
            type: "textarea",
            optional: true
          }
        ];
        
      case "Lost card":
        return [
          {
            id: "lost_date",
            text: "When did you lose your card?",
            type: "date",
            optional: false
          },
          {
            id: "lost_location",
            text: "Where did you lose your card?",
            type: "text",
            optional: true
          },
          {
            id: "lost_how",
            text: "How did you lose your card?",
            type: "text",
            optional: true
          },
          {
            id: "anything_else_lost",
            text: "Was anything else lost with your card?",
            type: "text",
            optional: true
          },
          {
            id: "authorized_others",
            text: "Have you authorized anyone else to use your card?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "authorized_who",
            text: "Who?",
            type: "text",
            optional: false,
            parentId: "authorized_others",
            parentCondition: "Yes"
          },
          {
            id: "additional_details",
            text: "Any additional details worth mentioning?",
            type: "textarea",
            optional: true
          }
        ];

      case "Stolen card":
        return [
          {
            id: "stolen_date",
            text: "When was the card stolen?",
            type: "date",
            optional: false
          },
          {
            id: "stolen_location",
            text: "Where was the card stolen?",
            type: "text",
            optional: true
          },
          {
            id: "stolen_how",
            text: "How was the card stolen?",
            type: "text",
            optional: true
          },
          {
            id: "anything_else_stolen",
            text: "Was anything else stolen?",
            type: "text",
            optional: true
          },
          {
            id: "authorized_others",
            text: "Have you authorized anyone else to use your card?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "authorized_who",
            text: "Who?",
            type: "text",
            optional: false,
            parentId: "authorized_others",
            parentCondition: "Yes"
          },
          {
            id: "additional_details",
            text: "Any additional details worth mentioning?",
            type: "textarea",
            optional: true
          }
        ];
        
        case "Card not received":
          return [
            {
              id: "expected_receive_date",
              text: "When did you expect to receive your card?",
              type: "date",
              optional: false
            },
            {
              id: "mail_access_others",
              text: "Does anyone else have access to your mail?",
              type: "radio",
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" }
              ],
              optional: false
            },
            {
              id: "mail_access_who",
              text: "Who has access to your mail?",
              type: "text",
              optional: true, // User can skip this
              parentId: "mail_access_others",
              parentCondition: "Yes"
            },
            {
              id: "card_intercepted",
              text: "Could anyone have intercepted the card?",
              type: "radio",
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" }
              ],
              optional: false
            },
            {
              id: "intercepted_by_who",
              text: "Who could have intercepted the card?",
              type: "text",
              optional: true, // User can skip this
              parentId: "card_intercepted",
              parentCondition: "Yes"
            },
            {
              id: "anything_else_not_received",
              text: "Was anything else not received?",
              type: "radio",
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" }
              ],
              optional: false
            },
            {
              id: "what_else_not_received",
              text: "What else was not received?",
              type: "text",
              optional: true, // User can skip this
              parentId: "anything_else_not_received",
              parentCondition: "Yes"
            },
            {
              id: "authorized_others_card_not_received", // Using a unique ID to avoid conflict if this question appears elsewhere
              text: "Have you authorized anyone else to use your card (once received)?",
              type: "radio",
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" }
              ],
              optional: false
            },
            {
              id: "authorized_who_card_not_received",
              text: "Who?",
              type: "text",
              optional: false, // This is mandatory if parent is "Yes"
              parentId: "authorized_others_card_not_received",
              parentCondition: "Yes"
            },
            {
              id: "additional_details_card_not_received",
              text: "Any additional details worth mentioning?",
              type: "textarea",
              optional: true
            }
          ];
        
      case "Merchandise not received":
        return [
          {
            id: "what_not_received",
            text: "What was not received?",
            type: "radio",
            options: [
              { value: "Service", label: "Service" },
              { value: "Product", label: "Product" }
            ],
            optional: false
          },
          {
            id: "expected_delivery_date",
            text: "When were you supposed to receive the merchandise?",
            type: "date",
            optional: false
          },
          {
            id: "evidence_of_delivery_date",
            text: "Do you have evidence of the expected delivery date?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "delivery_date_evidence",
            text: "Please upload evidence of expected delivery date",
            type: "file",
            optional: false,
            parentId: "evidence_of_delivery_date",
            parentCondition: "Yes"
          },
          {
            id: "delivery_issue",
            text: "Was the merchandise delivered late or to wrong location?",
            type: "radio",
            options: [
              { value: "Late", label: "Late" },
              { value: "Wrong location", label: "Wrong location" },
              { value: "Not delivered", label: "Not delivered" },
              { value: "Other", label: "Other" }
            ],
            optional: false
          },
          {
            id: "canceled_merchandise",
            text: "Did you cancel the merchandise?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "cancel_date",
            text: "When did you cancel?",
            type: "date",
            optional: false,
            parentId: "canceled_merchandise",
            parentCondition: "Yes"
          },
          {
            id: "cancellation_policy_provided",
            text: "Was a cancellation policy provided?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false,
            parentId: "canceled_merchandise",
            parentCondition: "Yes"
          },
          {
            id: "cancel_reason",
            text: "Why did you cancel?",
            type: "text",
            optional: false,
            placeholder: "Enter the reason for cancellation",
            parentId: "canceled_merchandise",
            parentCondition: "Yes"
          },
          {
            id: "merchant_cancel",
            text: "Did the merchant cancel the merchandise?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "merchant_cancel_date",
            text: "When did the merchant cancel the merchandise?",
            type: "date",
            optional: false,
            parentId: "merchant_cancel",
            parentCondition: "Yes"
          },
          {
            id: "resolve_with_merchant",
            text: "Did you try to resolve this with the merchant?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "merchant_resolution_evidence",
            text: "Upload evidence of communication with merchant",
            type: "file",
            optional: true,
            parentId: "resolve_with_merchant",
            parentCondition: "Yes"
          },
          {
            id: "order_confirmation",
            text: "Please provide evidence of the order confirmation or receipt",
            type: "file",
            optional: false
          }
        ];
        
      case "Cancelled recurring transaction":
        return [
          {
            id: "cancel_date",
            text: "When did you cancel your subscription?",
            type: "date",
            optional: false
          },
          {
            id: "cancel_method",
            text: "How did you request cancellation?",
            type: "text",
            optional: false
          },
          {
            id: "written_confirmation",
            text: "Do you have a written confirmation of cancellation?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "cancellation_confirmation",
            text: "Upload confirmation of cancellation",
            type: "file",
            optional: false,
            parentId: "written_confirmation",
            parentCondition: "Yes"
          },
          {
            id: "continued_use",
            text: "After cancellation, did you continue using services?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "cancellation_policy",
            text: "Provide a copy of the merchant's cancellation policy",
            type: "file",
            optional: true
          },
          {
            id: "additional_info",
            text: "Any additional information worth mentioning?",
            type: "textarea",
            optional: true
          }
        ];
        
      case "Incorrect amount billed":
        return [
          {
            id: "correct_amount",
            text: "What was the correct amount you agreed to be charged?",
            type: "text",
            optional: false
          },
          {
            id: "fee_explanation",
            text: "Was there a tip, fee, or tax that might explain the difference?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "merchant_contact",
            text: "Did you contact the merchant to resolve?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "merchant_response",
            text: "What was the merchant's response?",
            type: "text",
            optional: false,
            parentId: "merchant_contact",
            parentCondition: "Yes"
          },
          {
            id: "merchant_proof",
            text: "Upload any proof of communication",
            type: "file",
            optional: true,
            parentId: "merchant_contact",
            parentCondition: "Yes"
          },
          {
            id: "original_receipt",
            text: "Upload the original order confirmation or receipt",
            type: "file",
            optional: false
          },
          {
            id: "additional_details",
            text: "Any additional details worth mentioning?",
            type: "textarea",
            optional: true
          }
        ];
        
      case "Duplicate transaction":
        return [
          {
            id: "recognize_merchant",
            text: "Do you recognize the merchant name on the transactions?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "merchant_details",
            text: "Please share any more details about this merchant",
            type: "text",
            optional: true,
            parentId: "recognize_merchant",
            parentCondition: "Yes"
          },
          {
            id: "multiple_purchases",
            text: "Did you make multiple purchases from this merchant recently?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "purchase_details",
            text: "Please share additional details about those purchases",
            type: "text",
            optional: true,
            parentId: "multiple_purchases",
            parentCondition: "Yes"
          },
          {
            id: "subscription_related",
            text: "Are any of these charges related to a subscription or recurring payment?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "split_payment",
            text: "Was this a split payment or installment?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "merchant_informed",
            text: "Did the merchant inform you of this?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false,
            parentId: "split_payment",
            parentCondition: "Yes"
          },
          {
            id: "refund_promised",
            text: "Did the merchant promise to issue a refund?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "refund_proof",
            text: "Do you have proof of the refund or communication with the merchant?",
            type: "file",
            optional: true,
            parentId: "refund_promised",
            parentCondition: "Yes"
          },
          {
            id: "receipt_exists",
            text: "Do you have a receipt for the transaction?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "receipt_upload",
            text: "Please upload that evidence",
            type: "file",
            optional: true,
            parentId: "receipt_exists",
            parentCondition: "Yes"
          },
          {
            id: "additional_details",
            text: "Any additional details worth mentioning?",
            type: "textarea",
            optional: true
          }
        ];
        
      case "Dissatisfaction with product or service":
        return [
          {
            id: "received_date",
            text: "When did you receive the service or merchandise?",
            type: "date",
            optional: false
          },
          {
            id: "what_is_wrong",
            text: "What is wrong with the service or merchandise?",
            type: "radio", // Using radio for select-like behavior
            options: [
              { value: "Quality issue", label: "Quality issue" },
              { value: "Merchant misrepresented the sale", label: "Merchant misrepresented the sale" },
              { value: "Counterfeit", label: "Counterfeit" }
            ],
            optional: false
          },
          {
            id: "description_issue",
            text: "Please describe what happened to the product or service that you were dissatisfied with.",
            type: "textarea",
            optional: false
          },
          {
            id: "disputing_full_amount",
            text: "Are you disputing the full amount?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "disputed_amount_partial",
            text: "How much are you disputing?",
            type: "text", // Using text, further client-side validation for numbers/dot would be needed in a more complex setup
            optional: false,
            placeholder: "e.g., 25.50",
            parentId: "disputing_full_amount",
            parentCondition: "No"
          },
          {
            id: "attempted_resolution",
            text: "Did you try to resolve the issue with the merchant?",
            type: "radio",
            options: [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" }
            ],
            optional: false
          },
          {
            id: "contact_method",
            text: "How did you contact the merchant?",
            type: "text",
            optional: false,
            parentId: "attempted_resolution",
            parentCondition: "Yes"
          },
          {
            id: "contact_date",
            text: "When did you contact the merchant?",
            type: "date",
            optional: false,
            parentId: "attempted_resolution",
            parentCondition: "Yes"
          },
          {
            id: "merchant_response",
            text: "What was the merchant's response?",
            type: "textarea",
            optional: false,
            parentId: "attempted_resolution",
            parentCondition: "Yes"
          },
          {
            id: "send_evidence",
            text: "Please upload any communication or evidence of the damaged product or substandard service.",
            type: "file",
            optional: true
          },
          {
            id: "additional_details",
            text: "Any additional details worth mentioning?",
            type: "textarea",
            optional: true
          }
        ];
        
      case "Merchant credit not issued":
          return [
            {
              id: "what_paid_for",
              text: "What did you pay for?",
              type: "text",
              optional: false,
              placeholder: "e.g., Online course, shoes, flight ticket"
            },
            {
              id: "why_cancel_return",
              text: "Why did you cancel the service/returned the merchandise?",
              type: "textarea", // Textarea might be better for a potentially longer explanation
              optional: true,
              placeholder: "e.g., Product was defective, service not as described"
            },
            {
              id: "return_date",
              text: "When did you return the merchandise or cancel the service?",
              type: "date",
              optional: false
            },
            {
              id: "return_method",
              text: "How did you return the merchandise or cancel the service?",
              type: "text",
              optional: false
            },
            {
              id: "return_policy",
              text: "Was a return policy provided by the merchant?",
              type: "radio",
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" }
              ],
              optional: false
            },
            {
              id: "policy_upload",
              text: "Please upload the return policy.",
              type: "file",
              optional: true,
              parentId: "return_policy",
              parentCondition: "Yes"
            },
            {
              id: "provided_credit_evidence_type",
              text: "Were you provided a credit voucher, voided transaction receipt or refund acknowledgement?",
              type: "radio",
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" }
              ],
              optional: false
            },
            {
              id: "provided_credit_evidence_date",
              text: "When were you provided those?",
              type: "date",
              optional: false,
              parentId: "provided_credit_evidence_type",
              parentCondition: "Yes"
            },
            {
              id: "upload_provided_credit_evidence",
              text: "Please upload the evidence you were provided",
              type: "file",
              optional: false,
              parentId: "provided_credit_evidence_type",
              parentCondition: "Yes"
            },
            {
              id: "return_confirmation",
              text: "Please upload any return confirmation, email or receipt.", // Text updated
              type: "file",
              optional: false 
            },
            {
              id: "additional_details",
              text: "Any additional details worth mentioning?",
              type: "textarea",
              optional: true
            }
          ];
        
      default:
        return [];
    }
  }

  return (
    <Card className="mb-6 border-l-4 border-l-green-500 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle 
          className="text-lg font-semibold flex items-center cursor-pointer"
          onClick={() => selectedCardholder && selectedTransactions.length > 0 && setExpanded(!expanded)}
        >
          <AlertCircle className="mr-2 h-5 w-5 text-green-500" />
          Dispute Questions
          {expanded ? 
            <ChevronUp className="ml-2 h-4 w-4" /> : 
            <ChevronDown className="ml-2 h-4 w-4" />
          }
        </CardTitle>
      </CardHeader>
      
      <AnimatePresence>
        {expanded && selectedCardholder && selectedTransactions.length > 0 && !disabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="space-y-6 pb-6">
              {(hasPendingTransaction || hasExpiredTransaction) && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {hasPendingTransaction && (
                      <div className="text-yellow-700">
                        One or more transactions are still pending. Dispute processing may be delayed.
                      </div>
                    )}
                    {hasExpiredTransaction && (
                      <div className="text-red-700">
                        One or more transactions are over 90 days old. These disputes may require additional documentation.
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              
              {showQuestions && renderQuestions()}
              
              {showQuestions && (
                <div className="pt-4 flex justify-end">
                  <Button 
                    onClick={handlePreview}
                    disabled={!isReadyToPreview()}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Continue to Preview
                  </Button>
                </div>
              )}
            </CardContent>
          </motion.div>
        )}
        
        {!expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="py-2">
              <div className="flex flex-col">
                <div className="mt-2 text-sm">
                  <div className="flex items-center text-blue-600">
                    <Info className="h-3 w-3 mr-1" />
                    <span>{Object.keys(responses).length} questions answered</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </motion.div>
        )}
        
        {(disabled || !selectedCardholder || selectedTransactions.length === 0) && expanded && (
          <CardContent>
            <div className="text-center p-4 text-gray-500">
              {!selectedCardholder 
                ? "Please select a cardholder first"
                : selectedTransactions.length === 0 
                  ? "Please select at least one transaction" 
                  : "Please complete previous sections first"}
            </div>
          </CardContent>
        )}
      </AnimatePresence>
    </Card>
  );
}
